/**
 * Card Buff Provider - Manages equipped card bonuses for SLAP BATTLES
 * 
 * BUFF SYSTEM BY RARITY:
 * - Common: +10% Score Multiplier (Visual: Small sparks)
 * - Rare: 1x Shield per game (Visual: Blue bubble that pops on bomb hit)
 * - Legendary: +50% Score AND Slow Motion targets (Visual: Gold aura & Time warping)
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const CardBuffContext = createContext(null);

// Buff definitions by rarity
const BUFF_DEFINITIONS = {
  common: {
    name: 'Score Boost',
    description: '+10% Score Multiplier',
    scoreMultiplier: 1.10,
    hasShield: false,
    hasSlowMo: false,
    visualEffect: 'sparks',
    color: '#A0A0A0',
    icon: '⚡'
  },
  rare: {
    name: 'Shield Barrier',
    description: '1x Shield (blocks one bomb)',
    scoreMultiplier: 1.0,
    hasShield: true,
    shieldCount: 1,
    hasSlowMo: false,
    visualEffect: 'shield',
    color: '#00CCFF',
    icon: '🛡️'
  },
  epic: {
    name: 'Power Surge',
    description: '+25% Score + 1x Shield',
    scoreMultiplier: 1.25,
    hasShield: true,
    shieldCount: 1,
    hasSlowMo: false,
    visualEffect: 'surge',
    color: '#FF00FF',
    icon: '💥'
  },
  legendary: {
    name: 'Time Lord',
    description: '+50% Score + Slow Motion Targets',
    scoreMultiplier: 1.50,
    hasShield: false,
    hasSlowMo: true,
    slowMoFactor: 0.6, // Targets move 40% slower
    visualEffect: 'legendary',
    color: '#FFD700',
    icon: '👑'
  }
};

export const useCardBuff = () => {
  const context = useContext(CardBuffContext);
  if (!context) {
    throw new Error('useCardBuff must be used within CardBuffProvider');
  }
  return context;
};

export const CardBuffProvider = ({ children }) => {
  // Current equipped card from localStorage or user data
  const [equippedCard, setEquippedCard] = useState(null);
  const [activeBuff, setActiveBuff] = useState(null);
  
  // Game-specific state (resets each game)
  const [shieldActive, setShieldActive] = useState(false);
  const [shieldCount, setShieldCount] = useState(0);
  const [shieldJustUsed, setShieldJustUsed] = useState(false);

  // Load equipped card from localStorage on mount
  useEffect(() => {
    const loadEquippedCard = () => {
      try {
        const inventory = JSON.parse(localStorage.getItem('user_inventory') || '[]');
        const equipped = localStorage.getItem('equipped_card_id');
        
        if (equipped && inventory.length > 0) {
          const card = inventory.find(c => c.id === equipped);
          if (card) {
            setEquippedCard(card);
            const buff = BUFF_DEFINITIONS[card.rarity?.toLowerCase()] || BUFF_DEFINITIONS.common;
            setActiveBuff(buff);
            console.log('[CardBuff] Loaded equipped card:', card.character_name, 'Rarity:', card.rarity);
          }
        } else if (inventory.length > 0) {
          // Auto-equip best card if none equipped
          const sorted = [...inventory].sort((a, b) => {
            const order = { legendary: 4, epic: 3, rare: 2, common: 1 };
            return (order[b.rarity?.toLowerCase()] || 0) - (order[a.rarity?.toLowerCase()] || 0);
          });
          const best = sorted[0];
          if (best) {
            setEquippedCard(best);
            localStorage.setItem('equipped_card_id', best.id);
            const buff = BUFF_DEFINITIONS[best.rarity?.toLowerCase()] || BUFF_DEFINITIONS.common;
            setActiveBuff(buff);
            console.log('[CardBuff] Auto-equipped best card:', best.character_name);
          }
        }
      } catch (e) {
        console.error('[CardBuff] Error loading equipped card:', e);
      }
    };
    
    loadEquippedCard();
    
    // Listen for inventory updates
    const handleInventoryUpdate = () => loadEquippedCard();
    window.addEventListener('inventory-updated', handleInventoryUpdate);
    window.addEventListener('storage', handleInventoryUpdate);
    window.addEventListener('focus', handleInventoryUpdate);
    
    return () => {
      window.removeEventListener('inventory-updated', handleInventoryUpdate);
      window.removeEventListener('storage', handleInventoryUpdate);
      window.removeEventListener('focus', handleInventoryUpdate);
    };
  }, []);

  // Equip a specific card
  const equipCard = useCallback((card) => {
    if (!card) return;
    
    setEquippedCard(card);
    localStorage.setItem('equipped_card_id', card.id);
    
    const buff = BUFF_DEFINITIONS[card.rarity?.toLowerCase()] || BUFF_DEFINITIONS.common;
    setActiveBuff(buff);
    
    console.log('[CardBuff] Equipped:', card.character_name, 'Buff:', buff.name);
  }, []);

  // Initialize buffs at game start
  const initializeGameBuffs = useCallback(() => {
    if (!activeBuff) {
      console.log('[CardBuff] No active buff for game');
      return null;
    }
    
    // Reset shield state for new game
    if (activeBuff.hasShield) {
      setShieldActive(true);
      setShieldCount(activeBuff.shieldCount || 1);
    } else {
      setShieldActive(false);
      setShieldCount(0);
    }
    setShieldJustUsed(false);
    
    console.log('[CardBuff] Game initialized with buff:', activeBuff.name);
    return { ...activeBuff };
  }, [activeBuff]);

  // Use shield (returns true if shield absorbed hit)
  const useShield = useCallback(() => {
    if (shieldActive && shieldCount > 0) {
      const newCount = shieldCount - 1;
      setShieldCount(newCount);
      setShieldJustUsed(true);
      
      // Reset visual flag after animation
      setTimeout(() => setShieldJustUsed(false), 500);
      
      if (newCount <= 0) {
        setShieldActive(false);
      }
      
      console.log('[CardBuff] Shield used! Remaining:', newCount);
      return true;
    }
    return false;
  }, [shieldActive, shieldCount]);

  // Apply score multiplier
  const applyScoreMultiplier = useCallback((baseScore) => {
    if (!activeBuff) return baseScore;
    
    const multiplied = Math.round(baseScore * activeBuff.scoreMultiplier);
    if (activeBuff.scoreMultiplier > 1) {
      console.log(`[CardBuff] Score: ${baseScore} x ${activeBuff.scoreMultiplier} = ${multiplied}`);
    }
    return multiplied;
  }, [activeBuff]);

  // Get slow-mo factor for targets
  const getSlowMoFactor = useCallback(() => {
    if (!activeBuff || !activeBuff.hasSlowMo) return 1.0;
    return activeBuff.slowMoFactor || 0.6;
  }, [activeBuff]);

  // Get current buff info for HUD display
  const getBuffDisplayInfo = useCallback(() => {
    if (!activeBuff || !equippedCard) {
      return null;
    }
    
    return {
      name: activeBuff.name,
      description: activeBuff.description,
      icon: activeBuff.icon,
      color: activeBuff.color,
      visualEffect: activeBuff.visualEffect,
      cardName: equippedCard.character_name,
      cardRarity: equippedCard.rarity,
      cardImage: equippedCard.image_base64,
      shieldActive,
      shieldCount,
      shieldJustUsed,
      hasSlowMo: activeBuff.hasSlowMo,
      scoreMultiplier: activeBuff.scoreMultiplier
    };
  }, [activeBuff, equippedCard, shieldActive, shieldCount, shieldJustUsed]);

  const value = {
    equippedCard,
    activeBuff,
    equipCard,
    initializeGameBuffs,
    useShield,
    applyScoreMultiplier,
    getSlowMoFactor,
    getBuffDisplayInfo,
    shieldActive,
    shieldCount,
    shieldJustUsed,
    BUFF_DEFINITIONS
  };

  return (
    <CardBuffContext.Provider value={value}>
      {children}
    </CardBuffContext.Provider>
  );
};

export default CardBuffProvider;
